import L from 'leaflet';
import type { Ref } from 'vue';
import { unref } from 'vue';

interface UseMapResizeOptions {
  /**
   * Delay in milliseconds before updating map after resize
   */
  delay?: number;
}

export function useMapResize(
  map: Ref<L.Map | null>,
  mapContainer: Ref<HTMLElement | null>,
  options: UseMapResizeOptions = {}
): () => void {
  const { delay = 0 } = options;
  let resizeObserver: ResizeObserver | null = null;

  // If map or container is not defined, return an empty cleanup function
  if (!unref(map) || !unref(mapContainer)) {
    console.warn('Map or container is not defined for resize observer');
    return () => {};
  }

  // Create ResizeObserver to track container size changes
  resizeObserver = new ResizeObserver(() => {
    if (unref(map)) {
      // Use setTimeout to prevent frequent updates
      setTimeout(() => {
        unref(map)?.invalidateSize();
      }, delay);
    }
  });

  // Start observing the map container
  resizeObserver.observe(unref(mapContainer) as HTMLElement);

  // Return a function to stop the observer and free resources
  return () => {
    if (resizeObserver) {
      resizeObserver.disconnect();
      resizeObserver = null;
    }
  };
}
